/* Serinmola motion layer, loaded after pp-theme.js. Vanilla ES module, no dependencies.
   Effects only touch transform/opacity (or CSS variables that feed them) and all of them
   are skipped under prefers-reduced-motion. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

// On desktop Horizon locks html/body and scrolls .page-wrapper; on phones the window scrolls.
const scroller = () => {
  const pw = document.querySelector('.page-wrapper');
  return pw && pw.scrollHeight > pw.clientHeight + 4 && getComputedStyle(pw).overflowY !== 'visible' ? pw : document.scrollingElement;
};

const onScroll = (fn) => {
  let queued = false;
  const run = () => {
    queued = false;
    fn();
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  };
  document.addEventListener('scroll', queue, { passive: true, capture: true });
  window.addEventListener('resize', queue, { passive: true });
  run();
};

const inView = (el, cb) => {
  if (!('IntersectionObserver' in window)) return cb(true);
  new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting)), { threshold: 0.15 }).observe(el);
};

/* ---------- 15-minute "mola" timer: a fast-forwarded countdown while visible ---------- */
function initTimer(root) {
  root.querySelectorAll('[data-sm-timer]').forEach((el) => {
    const total = (Number(el.dataset.minutes) || 15) * 60;
    const val = el.querySelector('[data-sm-timer-val]');
    const C = 113.1;
    let left = total;
    let id = null;
    const paint = () => {
      const m = Math.floor(left / 60);
      const s = left % 60;
      val.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      el.style.setProperty('--sm-off', ((1 - left / total) * C).toFixed(2));
    };
    paint();
    if (reduceMotion) return;
    const tick = () => {
      left -= 3;
      if (left <= 0) left = total;
      paint();
    };
    inView(el, (on) => {
      if (on && !id) id = setInterval(tick, 120);
      if (!on && id) {
        clearInterval(id);
        id = null;
      }
    });
  });
}

/* ---------- "Soğuk ya da sıcak" chip ---------- */
function initTemp(root) {
  if (reduceMotion) return;
  root.querySelectorAll('[data-sm-temp]').forEach((chip) => {
    const txt = chip.querySelector('[data-sm-temp-txt]');
    let hot = false;
    setInterval(() => {
      chip.classList.add('is-swap');
      setTimeout(() => {
        hot = !hot;
        txt.textContent = hot ? 'Sıcak' : 'Soğuk';
        chip.classList.toggle('is-hot', hot);
        chip.classList.remove('is-swap');
      }, 260);
    }, 2600);
  });
}

/* ---------- Hero tilt (pointer) + parallax (scroll) ---------- */
function initHeroDepth(root) {
  // Phones: no tilt or parallax, the hero must stay perfectly still while scrolling.
  if (reduceMotion || window.innerWidth < 990) return;
  root.querySelectorAll('[data-sm-tilt]').forEach((art) => {
    const img = art.querySelector('.pp-hero__img');
    if (!img) return;
    if (finePointer) {
      art.addEventListener('pointermove', (e) => {
        const b = art.getBoundingClientRect();
        const x = (e.clientX - b.left) / b.width - 0.5;
        const y = (e.clientY - b.top) / b.height - 0.5;
        img.style.setProperty('--sm-ry', `${(x * 10).toFixed(2)}deg`);
        img.style.setProperty('--sm-rx', `${(-y * 8).toFixed(2)}deg`);
      });
      art.addEventListener('pointerleave', () => {
        img.style.setProperty('--sm-ry', '0deg');
        img.style.setProperty('--sm-rx', '0deg');
      });
    }
    const hero = art.closest('[data-sm-parallax]');
    if (!hero) return;
    onScroll(() => {
      const b = hero.getBoundingClientRect();
      if (b.bottom < 0 || b.top > window.innerHeight) return;
      art.style.setProperty('--sm-py', `${Math.max(-60, Math.min(60, -b.top * 0.12)).toFixed(1)}px`);
    });
  });
}

/* ---------- Magnetic buttons ---------- */
function initMagnet(root) {
  if (reduceMotion || !finePointer) return;
  root.querySelectorAll('[data-sm-magnet]').forEach((btn) => {
    btn.addEventListener('pointermove', (e) => {
      const b = btn.getBoundingClientRect();
      btn.style.setProperty('--sm-mx', `${((e.clientX - b.left - b.width / 2) * 0.18).toFixed(1)}px`);
      btn.style.setProperty('--sm-my', `${((e.clientY - b.top - b.height / 2) * 0.28).toFixed(1)}px`);
    });
    btn.addEventListener('pointerleave', () => {
      btn.style.setProperty('--sm-mx', '0px');
      btn.style.setProperty('--sm-my', '0px');
    });
  });
}

/* ---------- Bundle pick feedback ---------- */
function initBundlePop(root) {
  if (reduceMotion) return;
  root.querySelectorAll('input[name="pp-bundle"]').forEach((input) => {
    input.addEventListener('change', () => {
      const label = input.closest('.pp-bundle');
      if (!label) return;
      label.classList.remove('sm-pop');
      void label.offsetWidth;
      label.classList.add('sm-pop');
    });
  });
}

/* ---------- Reading progress bar ---------- */
function initProgress() {
  if (reduceMotion || document.querySelector('.sm-progress')) return;
  const bar = document.createElement('div');
  bar.className = 'sm-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  onScroll(() => {
    const s = scroller();
    const max = s.scrollHeight - s.clientHeight;
    bar.style.setProperty('--sm-p', max > 0 ? (s.scrollTop / max).toFixed(4) : 0);
  });
}

/* ---------- Legal and help pages: cards, table of contents, highlights ---------- */
function initLegal(root) {
  root.querySelectorAll('[data-sm-legal-body]').forEach((body) => {
    if (body.dataset.smReady) return;
    body.dataset.smReady = '1';
    const scope = body.querySelector('.shopify-policy__body .rte') || body.querySelector('.shopify-policy__body') || body;

    // Group the text under each h2 (or h3 when a page has no h2s) into a card.
    let heads = [...scope.children].filter((n) => n.tagName === 'H2');
    if (heads.length < 2) heads = [...scope.children].filter((n) => n.tagName === 'H3');
    const cards = [];
    const makeCard = (nodes, head, i) => {
      const real = nodes.filter((n) => n.nodeType === 1 || n.textContent.trim());
      if (!real.length) return;
      const card = document.createElement('section');
      card.className = 'sm-card' + (head ? '' : ' sm-card--intro');
      card.style.setProperty('--k', String(Math.min(cards.length, 8)));
      card.id = 'sm-b-' + i;
      nodes[0].before(card);
      nodes.forEach((n) => card.appendChild(n));
      cards.push({ card, head });
    };
    const kids = [...scope.childNodes];
    let group = [];
    let current = null;
    let idx = 0;
    kids.forEach((n) => {
      if (heads.includes(n)) {
        if (group.length) makeCard(group, current, idx++);
        group = [n];
        current = n;
      } else group.push(n);
    });
    if (group.length) makeCard(group, current, idx++);

    // Highlight the phrases a shopper looks for, outside links.
    const re = /(14 gün|kapıda ödeme|kapıda ödemeli|ücretsiz kargo|kargo ücretsiz(?:dir)?|cayma hakkı)/gi;
    const reTest = new RegExp(re.source, 'i');
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode: (t) => (t.parentElement.closest('a, mark, h2, h3') || !reTest.test(t.data) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    const hits = [];
    while (walker.nextNode()) hits.push(walker.currentNode);
    hits.forEach((t) => {
      re.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let last = 0;
      t.data.replace(re, (m, _g, off) => {
        frag.append(t.data.slice(last, off));
        const mark = document.createElement('mark');
        mark.className = 'sm-hl';
        mark.textContent = m;
        frag.append(mark);
        last = off + m.length;
        return m;
      });
      frag.append(t.data.slice(last));
      t.replaceWith(frag);
    });

    // "İçindekiler" chips with a scroll spy.
    const toc = body.parentElement.querySelector('[data-sm-toc]');
    const headed = cards.filter((c) => c.head);
    if (!toc || headed.length < 3) return;
    headed.forEach(({ card, head }) => {
      const a = document.createElement('a');
      a.href = '#' + card.id;
      a.textContent = head.textContent.replace(/^\s*\d+[.)]\s*/, '').trim();
      a.addEventListener('click', (e) => {
        e.preventDefault();
        card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
      toc.appendChild(a);
    });
    toc.hidden = false;
    const links = [...toc.children];
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const i = headed.findIndex((h) => h.card === en.target);
          links.forEach((l, j) => l.classList.toggle('is-on', j === i));
          const l = links[i];
          if (l) toc.scrollTo({ left: l.offsetLeft - 16, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    headed.forEach((h) => io.observe(h.card));
  });
}

/* ---------- Delivery timeline: the truck loop runs only while the block is on screen ---------- */
function initShip(root) {
  root.querySelectorAll('[data-sm-ship]').forEach((el) => {
    if (el.dataset.smShipOn) return;
    el.dataset.smShipOn = '1';
    inView(el, (on) => el.classList.toggle('is-live', on));
  });
}

function boot(root = document) {
  initShip(root);
  initTimer(root);
  initTemp(root);
  initHeroDepth(root);
  initMagnet(root);
  initBundlePop(root);
  initLegal(root);
}

boot();
initProgress();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
